import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import useUserDetails from '../../hooks/useUserDetails';
import useErrorModal from '../../hooks/useErrorModal';
import { postChat } from '../_shared/chatService';
import { buildChatRequest, ScreenContextSlice, ChatIntent } from './buildChatRequest';
import { formatFileSize } from './formatFileSize';
import {
  INITIAL_DRAFTS,
  INITIAL_SECTION_STATUSES,
  OUTLINE_SECTIONS,
  NarrativeOptionId,
  OutlineSection,
  SectionMeta,
  SectionStatus,
} from './prototype2Data';
import { outlineToReportSections } from './outlineToReportSections';
import { parseOutlineResponse } from './parseOutlineResponse';
import { parseParagraphResponse } from './parseParagraphResponse';

const CHAT_SCOPE = 'mock-main';

type ScreenId = '01-define-narrative' | '02-review-outline' | '03-draft-report';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  outlineSections?: OutlineSection[];
}

interface UploadedFile {
  id: string;
  name: string;
  size: string;
}

interface Prototype2ContextValue {
  currentScreen: ScreenId;
  goToScreen: (id: ScreenId) => void;
  returnToNarrative: () => void;
  chatHistory: ChatMessage[];
  paragraphChatHistories: Record<string, ChatMessage[]>;
  isChatPending: boolean;
  pendingParagraphId: string | null;
  updatedParagraphId: string | null;
  beginParagraphDraft: (paragraphId: string) => void;
  sendChat: (message: string, slice: ScreenContextSlice) => Promise<void>;
  drafts: Record<string, string>;
  sectionStatuses: Record<string, SectionStatus>;
  selectedNarrative: NarrativeOptionId | null;
  setSelectedNarrative: (id: NarrativeOptionId | null) => void;
  uploadedFiles: UploadedFile[];
  uploadPdfFile: (file: File) => void;
  removeUploadedFile: (id: string) => void;
  contextText: string;
  setContextText: (text: string) => void;
  expandedParagraphIds: Set<string>;
  toggleParagraphExpanded: (paragraphId: string) => void;
  customNarrativeText: string;
  setCustomNarrativeText: (text: string) => void;
  outlineSections: OutlineSection[];
  reportSections: SectionMeta[];
  approveOutlineForDrafting: () => SectionMeta[];
}

const DRAFT_ONLY_INTENTS: ReadonlySet<ChatIntent> = new Set<ChatIntent>(['draft']);
const PARAGRAPH_REVISION_INTENTS: ReadonlySet<ChatIntent> = new Set<ChatIntent>(['edit-current-draft', 'expand']);

const Prototype2Context = createContext<Prototype2ContextValue | null>(null);

interface Prototype2ProviderProps {
  children: ReactNode;
}

const Prototype2Provider = ({ children }: Prototype2ProviderProps) => {
  const [currentScreen, setCurrentScreen] = useState<ScreenId>('01-define-narrative');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [paragraphChatHistories, setParagraphChatHistories] = useState<Record<string, ChatMessage[]>>({});
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [isChatPending, setIsChatPending] = useState(false);
  const [pendingParagraphId, setPendingParagraphId] = useState<string | null>(null);
  const [updatedParagraphId, setUpdatedParagraphId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>(INITIAL_DRAFTS);
  const [sectionStatuses, setSectionStatuses] = useState<Record<string, SectionStatus>>(INITIAL_SECTION_STATUSES);
  const [selectedNarrative, setSelectedNarrative] = useState<NarrativeOptionId | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [contextText, setContextText] = useState<string>('');
  const [expandedParagraphIds, setExpandedParagraphIds] = useState<Set<string>>(
    new Set<string>(['college-eligibility__p1'])
  );
  const [customNarrativeText, setCustomNarrativeText] = useState<string>('');
  const [outlineSections, setOutlineSections] = useState<OutlineSection[]>(OUTLINE_SECTIONS);
  const [reportSections, setReportSections] = useState<SectionMeta[]>([]);
  const chatRequestGenerationRef = useRef(0);

  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const goToScreen = useCallback((id: ScreenId) => {
    setCurrentScreen(id);
  }, []);

  const isChatRequestStale = useCallback(
    (generationAtStart: number) => generationAtStart !== chatRequestGenerationRef.current,
    []
  );

  const returnToNarrative = useCallback(() => {
    chatRequestGenerationRef.current += 1;
    setChatHistory([]);
    setParagraphChatHistories({});
    setConversationId(undefined);
    setIsChatPending(false);
    setPendingParagraphId(null);
    setOutlineSections(OUTLINE_SECTIONS);
    setReportSections([]);
    setCurrentScreen('01-define-narrative');
  }, []);

  const uploadPdfFile = useCallback((file: File) => {
    setUploadedFiles((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: file.name,
        size: formatFileSize(file.size),
      },
    ]);
  }, []);

  const removeUploadedFile = useCallback((id: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  useEffect(() => {
    if (!updatedParagraphId) return;
    const timer = window.setTimeout(() => setUpdatedParagraphId(null), 4000);
    return () => window.clearTimeout(timer);
  }, [updatedParagraphId]);

  const toggleParagraphExpanded = useCallback((paragraphId: string) => {
    setExpandedParagraphIds((prev) => {
      const next = new Set(prev);
      if (next.has(paragraphId)) next.delete(paragraphId);
      else next.add(paragraphId);
      return next;
    });
  }, []);

  const beginParagraphDraft = useCallback((paragraphId: string) => {
    setUpdatedParagraphId(null);
    setPendingParagraphId(paragraphId);
    setDrafts((prev) => ({ ...prev, [paragraphId]: '' }));
  }, []);

  const approveOutlineForDrafting = useCallback(() => {
    const sections = outlineToReportSections(outlineSections);
    setReportSections(sections);
    setSectionStatuses(Object.fromEntries(sections.map((s) => [s.id, s.status])));
    setDrafts({});
    setParagraphChatHistories({});

    const firstParagraphId = sections[0]?.paragraphs[0]?.id;
    if (firstParagraphId) {
      setExpandedParagraphIds(new Set([firstParagraphId]));
    }

    return sections;
  }, [outlineSections]);

  const sendChat = useCallback(
    async (message: string, slice: ScreenContextSlice) => {
      const trimmed = message.trim();
      if (!trimmed) return;

      const generationAtStart = chatRequestGenerationRef.current;
      const targetParagraphId = slice.active_section_id;
      const isInitialParagraphDraft = DRAFT_ONLY_INTENTS.has(slice.intent) && Boolean(targetParagraphId);
      const isParagraphRevision = PARAGRAPH_REVISION_INTENTS.has(slice.intent) && Boolean(targetParagraphId);
      if (isParagraphRevision && targetParagraphId) {
        setParagraphChatHistories((prev) => ({
          ...prev,
          [targetParagraphId]: [...(prev[targetParagraphId] ?? []), { role: 'user', content: trimmed }],
        }));
      } else if (!isInitialParagraphDraft) {
        setChatHistory((prev) => [...prev, { role: 'user', content: trimmed }]);
      }
      setIsChatPending(true);
      if ((isInitialParagraphDraft || isParagraphRevision) && targetParagraphId) {
        setUpdatedParagraphId(null);
        setPendingParagraphId(targetParagraphId);
      }

      try {
        const eoaId = userDetails?.eoaId;
        const accessToken = userDetails?.access_token ?? '';
        if (!eoaId || !accessToken) {
          throw new Error('Missing EOA or access token');
        }

        const payload = buildChatRequest({
          message: trimmed,
          screenId: slice.screen_id ?? currentScreen,
          slice,
          conversationId,
          scope: CHAT_SCOPE,
          sectionStatuses,
          uploadedFiles,
          contextText,
          outlineSections,
          reportSections,
          eoaId,
        });
        const { conversation_id, response } = await postChat(payload, accessToken);
        if (isChatRequestStale(generationAtStart)) return;

        setConversationId(conversation_id);

        if (isInitialParagraphDraft && targetParagraphId && response.trim()) {
          const parsed = parseParagraphResponse(response);
          if (parsed.body) {
            setDrafts((prev) => ({ ...prev, [targetParagraphId]: parsed.body as string }));
            setUpdatedParagraphId(targetParagraphId);
          }
        } else if (isParagraphRevision && targetParagraphId) {
          const parsed = parseParagraphResponse(response);
          if (parsed.body) {
            setDrafts((prev) => ({ ...prev, [targetParagraphId]: parsed.body as string }));
            setUpdatedParagraphId(targetParagraphId);
          }
          const chatText = parsed.chat || (parsed.body ? 'Paragraph updated.' : '');
          if (chatText) {
            setParagraphChatHistories((prev) => ({
              ...prev,
              [targetParagraphId]: [...(prev[targetParagraphId] ?? []), { role: 'assistant', content: chatText }],
            }));
          }
        } else {
          const isOutlineRevision = slice.response_hint?.shape === 'outline-revision';
          const parsed = isOutlineRevision ? parseOutlineResponse(response) : null;
          const revisedSections = parsed?.sections;
          if (revisedSections?.length) {
            setOutlineSections(revisedSections);
          }
          const assistantContent = revisedSections?.length && parsed ? parsed.chat : response;
          setChatHistory((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: assistantContent,
              ...(revisedSections?.length ? { outlineSections: revisedSections } : {}),
            },
          ]);
        }
      } catch (error) {
        if (isChatRequestStale(generationAtStart)) return;
        handleOpenErrorModal('Prototype chat request failed', error as Error);
        if (isParagraphRevision && targetParagraphId) {
          setParagraphChatHistories((prev) => ({
            ...prev,
            [targetParagraphId]: [
              ...(prev[targetParagraphId] ?? []),
              { role: 'assistant', content: '(chat backend unavailable — this is a prototype)' },
            ],
          }));
        } else if (!isInitialParagraphDraft) {
          setChatHistory((prev) => [
            ...prev,
            { role: 'assistant', content: '(chat backend unavailable — this is a prototype)' },
          ]);
        }
      } finally {
        if (!isChatRequestStale(generationAtStart)) {
          setIsChatPending(false);
          setPendingParagraphId(null);
        }
      }
    },
    [
      chatHistory,
      paragraphChatHistories,
      conversationId,
      currentScreen,
      sectionStatuses,
      uploadedFiles,
      contextText,
      outlineSections,
      reportSections,
      userDetails,
      handleOpenErrorModal,
      isChatRequestStale,
    ]
  );

  const value = useMemo<Prototype2ContextValue>(
    () => ({
      currentScreen,
      goToScreen,
      returnToNarrative,
      chatHistory,
      paragraphChatHistories,
      isChatPending,
      pendingParagraphId,
      updatedParagraphId,
      beginParagraphDraft,
      sendChat,
      drafts,
      sectionStatuses,
      selectedNarrative,
      setSelectedNarrative,
      uploadedFiles,
      uploadPdfFile,
      removeUploadedFile,
      contextText,
      setContextText,
      expandedParagraphIds,
      toggleParagraphExpanded,
      customNarrativeText,
      setCustomNarrativeText,
      outlineSections,
      reportSections,
      approveOutlineForDrafting,
    }),
    [
      currentScreen,
      goToScreen,
      returnToNarrative,
      chatHistory,
      paragraphChatHistories,
      isChatPending,
      pendingParagraphId,
      updatedParagraphId,
      beginParagraphDraft,
      sendChat,
      drafts,
      sectionStatuses,
      selectedNarrative,
      uploadedFiles,
      uploadPdfFile,
      removeUploadedFile,
      contextText,
      expandedParagraphIds,
      toggleParagraphExpanded,
      customNarrativeText,
      outlineSections,
      reportSections,
      approveOutlineForDrafting,
    ]
  );

  return <Prototype2Context.Provider value={value}>{children}</Prototype2Context.Provider>;
};

const usePrototype2 = () => {
  const ctx = useContext(Prototype2Context);
  if (!ctx) throw new Error('usePrototype2 must be used inside Prototype2Provider');
  return ctx;
};

export { Prototype2Provider, usePrototype2 };
export type { ScreenId, ChatMessage, UploadedFile };
